import { Link } from "react-router-dom";
import { Button } from "../../components/ui/button";
import { trackCtaClick } from "../../lib/analytics";
import {
  MarketingShellLayout,
  ValueGrid,
  type ValueItem,
} from "../../components/marketing/MarketingShell";
import { Seo } from "../../seo/Seo";

const VALUES: ValueItem[] = [
  {
    tag: "GROUP",
    title: "Folders, not a junk drawer.",
    body: "A folder is a typed schema. Every flag inside it shares the same fields — boolean, number, enum, string. Approval rules and rollout caps live on the folder.",
  },
  {
    tag: "RECORDS",
    title: "One schema, many overrides.",
    body: "Records are rows: a plan tier, a customer, a region. Each record sets values for the folder’s flags. Look up by ID; values fall back to defaults if untouched.",
  },
  {
    tag: "READ",
    title: "Plain HTTP, no client required.",
    body: "Read flag values over a small REST surface. Cache however your stack already caches; the API is the contract — nothing more to install.",
  },
];

const TABLE_ROWS = [
  { r: "plan:starter", vals: ["3", "false", "false", "community"] },
  { r: "plan:growth", vals: ["10", "false", "true", "email"] },
  { r: "plan:pro", vals: ["25", "true", "true", "priority"] },
  {
    r: "plan:enterprise",
    vals: ["∞", "true", "true", "dedicated"],
    hi: true,
  },
];

export function FlagsMarketingPage() {
  return (
    <MarketingShellLayout active="Flags">
      <Seo route="/flags" />
      {/* Hero */}
      <section>
        <div className="max-w-7xl mx-auto w-full px-5 sm:px-12 pt-12 sm:pt-16 pb-10 grid lg:grid-cols-[1.1fr_1fr] gap-12 lg:gap-14 items-start">
          <div>
            <span className="inline-flex items-center gap-1.5 font-mono text-[10px] tracking-[0.12em] uppercase text-signal-ok">
              <span className="size-1.5 rounded-sm bg-signal-ok" />
              02 · Feature flags · for the calm side of shipping
            </span>
            <h1 className="mt-5 text-5xl sm:text-6xl lg:text-[5rem] font-semibold leading-[0.96] tracking-[-0.04em]">
              Flags PMs can flip,
              <br />
              <span className="font-serif italic font-normal">
                devs can
              </span>{" "}
              <span className="text-signal-ok">trust.</span>
            </h1>
            <p className="mt-6 text-base sm:text-lg leading-relaxed text-muted-foreground max-w-xl font-serif">
              Group flags into folders. Records inside a folder share the same
              schema — different values per customer, plan, or region. Read
              values over HTTP in milliseconds.
            </p>
            <div className="mt-7 flex flex-wrap gap-3 items-center">
              <Link
                to="/register"
                onClick={() =>
                  trackCtaClick("hero", {
                    target: "register",
                    surface: "Flags",
                  })
                }
              >
                <Button size="lg">Start free →</Button>
              </Link>
              <a
                href="/#playground"
                onClick={() => trackCtaClick("sub_nav", { surface: "Flags" })}
                className="text-sm font-medium underline underline-offset-4 decoration-rule hover:decoration-foreground"
              >
                Try the live demo →
              </a>
            </div>
          </div>

          {/* Hero — entitlements table */}
          <div className="self-start w-full">
            <div className="bg-card-paper border border-rule rounded-xl overflow-hidden shadow-[0_18px_42px_-22px_rgba(27,31,42,0.18)]">
              <div className="px-3.5 py-2.5 border-b border-rule-soft flex items-center gap-2 bg-panel">
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 14 14"
                  fill="none"
                  aria-hidden="true"
                >
                  <path
                    d="M1 3.5C1 2.67 1.67 2 2.5 2h2.8l1 1.2H11.5c.83 0 1.5.67 1.5 1.5v6c0 .83-.67 1.5-1.5 1.5h-9c-.83 0-1.5-.67-1.5-1.5v-7Z"
                    className="fill-signal-ok stroke-signal-ok"
                    strokeWidth="0.8"
                  />
                </svg>
                <span className="font-mono text-[11px] text-muted-foreground font-semibold">
                  Plan entitlements
                </span>
                <span className="font-mono text-[10px] text-faint">
                  · 5 flags × 4 records
                </span>
              </div>
              <table className="w-full border-collapse">
                <thead>
                  <tr>
                    <th className="px-3 py-2 text-left font-mono text-[10px] text-faint border-b border-rule-soft font-normal">
                      record
                    </th>
                    {["max_seats", "sso", "audit", "support"].map((c) => (
                      <th
                        key={c}
                        className="px-2.5 py-2 text-left font-mono text-[10px] text-faint border-b border-rule-soft font-normal"
                      >
                        {c}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {TABLE_ROWS.map((r) => (
                    <tr
                      key={r.r}
                      className={r.hi ? "bg-signal-red-soft/60" : ""}
                    >
                      <td className="px-3 py-2.5 font-mono text-[11.5px] border-b border-rule-soft">
                        {r.r}
                      </td>
                      {r.vals.map((v, vi) => (
                        <td
                          key={vi}
                          className="px-2.5 py-2.5 font-mono text-[11.5px] border-b border-rule-soft"
                        >
                          {v === "true" ? (
                            <span className="text-signal-ok font-semibold">
                              ● true
                            </span>
                          ) : v === "false" ? (
                            <span className="text-faint">○ false</span>
                          ) : (
                            v
                          )}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="px-3.5 py-2.5 border-t border-rule-soft font-mono text-[11px] text-faint bg-panel">
                GET /api/projects/:projectId/flags/
                <span className="text-signal-red">entitlements</span>?record=
                <span className="text-signal-red">cus_8x9k2L</span> →{" "}
                <span className="text-signal-ok">{`{ max_seats: 50 }`}</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      <ValueGrid items={VALUES} accent="var(--color-signal-ok)" />

      {/* API sample */}
      <section className="border-t border-rule bg-card-paper">
        <div className="max-w-7xl mx-auto w-full px-5 sm:px-12 py-10 sm:py-12">
          <div className="font-mono text-[10px] tracking-[0.14em] uppercase text-faint mb-3">
            API · REST
          </div>
          <div className="grid lg:grid-cols-[1.1fr_1fr] gap-7 lg:gap-12 items-center">
            <pre className="m-0 p-5 bg-ink text-paper rounded-xl font-mono text-[13px] leading-[1.7] overflow-auto">
{`# Look up a record's values for one folder
curl https://api.swisstools.dev/api/projects/$PROJECT_ID/flags \\
  -H "Authorization: Bearer $TOKEN" \\
  -G --data-urlencode "folder=entitlements" \\
       --data-urlencode "record=cus_8x9k2L"

# → 200 OK
# { "max_seats": 50, "sso": true, "audit": true,
#   "support": "dedicated" }

# Read a single flag value
curl https://api.swisstools.dev/api/projects/$PROJECT_ID/flags/checkout-experiment-b \\
  -H "Authorization: Bearer $TOKEN" \\
  -G --data-urlencode "userId=u_123"   # → true (25% rollout)`}
            </pre>
            <div>
              <div className="font-serif text-[22px] font-semibold tracking-tight mb-2.5">
                Plain HTTP, no client required.
              </div>
              <p className="text-sm leading-relaxed text-muted-foreground m-0">
                Two endpoints cover the common cases:{" "}
                <code className="font-mono bg-panel px-1 rounded">
                  /flags
                </code>{" "}
                returns every value for a record (your customer's plan, region,
                tenant);{" "}
                <code className="font-mono bg-panel px-1 rounded">
                  /flags/:key
                </code>{" "}
                returns a single flag, with percentage rollouts and A/B
                targeting resolved server-side.
              </p>
            </div>
          </div>
        </div>
      </section>
    </MarketingShellLayout>
  );
}
